export type LaunchCastEntry = {
  label: string
  memberNames: string[]
  memberIds: string[]
  relationshipId?: string
  returning?: boolean
}

export type LaunchShow = {
  id: 'other-way' | 'hunt-for-love'
  title: string
  fullTitle: string
  season: number
  nightLabel: string
  weekday: number
  hourEastern: number
  premiereIso: string
  roomSlug: string
  accent: 'plum' | 'coral'
  cast: LaunchCastEntry[]
}

export const launchShows: LaunchShow[] = [
  {
    id: 'other-way',
    title: 'The Other Way',
    fullTitle: '90 Day Fiance: The Other Way',
    season: 8,
    nightLabel: 'Sunday nights',
    weekday: 0,
    hourEastern: 20,
    premiereIso: '2026-10-05T00:00:00.000Z',
    roomSlug: 'other-way-s8-weekly-watch-along',
    accent: 'plum',
    cast: [
      { label: 'Kenny & Armando', memberNames: ['Kenny', 'Armando'], memberIds: ['p-kenneth-niedermeier', 'p-armando-rubio'], relationshipId: 'ARC-7DD923A1934C', returning: true },
      { label: 'Niles & Matilda', memberNames: ['Niles', 'Matilda'], memberIds: ['p-niles-valentine', 'p-matilda-nti'], relationshipId: 'ARC-6C37F116667F', returning: true },
      { label: 'Jonathan & Ally', memberNames: ['Jonathan', 'Ally'], memberIds: ['p-jonathan-ally-arc', 'p-ally-jonathan-arc'], relationshipId: 'ARC-613995429020' },
      { label: 'Zuri & Ben', memberNames: ['Zuri', 'Ben'], memberIds: ['p-zuri-ben-arc', 'p-ben-zuri-arc'], relationshipId: 'ARC-2F1D8514A67A' },
      { label: 'Jessy & Vittorio', memberNames: ['Jessy', 'Vittorio'], memberIds: ['p-jessy-vittorio-arc', 'p-vittorio-jessy-arc'], relationshipId: 'ARC-554CAC8794EF' },
      { label: 'Michelle & Felix', memberNames: ['Michelle', 'Felix'], memberIds: ['p-michelle-felix-arc', 'p-felix-michelle-arc'], relationshipId: 'ARC-464FCC3AA26C' },
      { label: 'Andi & Nigel', memberNames: ['Andi', 'Nigel'], memberIds: ['p-andi-nigel-arc', 'p-nigel-andi-arc'], relationshipId: 'ARC-D428916C0C37' },
    ],
  },
  {
    id: 'hunt-for-love',
    title: 'Hunt for Love',
    fullTitle: '90 Day: Hunt for Love',
    season: 2,
    nightLabel: 'Monday nights',
    weekday: 1,
    hourEastern: 20,
    premiereIso: '2026-10-06T00:00:00.000Z',
    roomSlug: 'hunt-for-love-s2-weekly-watch-along',
    accent: 'coral',
    cast: [
      { label: 'Brittany', memberNames: ['Brittany'], memberIds: ['p-brittany-banks'], returning: true },
      { label: 'Chloe', memberNames: ['Chloe'], memberIds: ['p-chloe'], returning: true },
      { label: 'Jamal', memberNames: ['Jamal'], memberIds: ['p-jamal-menzies'], returning: true },
      { label: 'Kara', memberNames: ['Kara'], memberIds: ['p-kara-bass'], returning: true },
      { label: 'Michael', memberNames: ['Michael'], memberIds: ['p-michael-ilesanmi'], returning: true },
      { label: 'Steven', memberNames: ['Steven'], memberIds: ['p-steven-frend'], returning: true },
      { label: 'Emma', memberNames: ['Emma'], memberIds: ['p-emma-b90-s8'], returning: true },
    ],
  },
]

const easternPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

function zonedParts(date: Date) {
  const values = Object.fromEntries(easternPartsFormatter.formatToParts(date).map((part) => [part.type, part.value]))
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(values.weekday),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  }
}

function easternDateTimeToUtc(year: number, month: number, day: number, hour: number) {
  const desiredAsUtc = Date.UTC(year, month - 1, day, hour, 0, 0)
  let guess = desiredAsUtc

  // Resolve the New York offset at this date. Repeating handles DST boundaries.
  for (let index = 0; index < 3; index += 1) {
    const actual = zonedParts(new Date(guess))
    const actualAsUtc = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second)
    guess += desiredAsUtc - actualAsUtc
  }

  return new Date(guess)
}

export function nextAiringFor(show: LaunchShow, now = new Date()) {
  const premiere = new Date(show.premiereIso)
  if (now < premiere) return premiere

  const easternNow = zonedParts(now)
  let daysAhead = (show.weekday - easternNow.weekday + 7) % 7
  const calendarDate = new Date(Date.UTC(easternNow.year, easternNow.month - 1, easternNow.day + daysAhead))
  let candidate = easternDateTimeToUtc(
    calendarDate.getUTCFullYear(),
    calendarDate.getUTCMonth() + 1,
    calendarDate.getUTCDate(),
    show.hourEastern,
  )

  if (candidate <= now) {
    daysAhead += 7
    const nextCalendarDate = new Date(Date.UTC(easternNow.year, easternNow.month - 1, easternNow.day + daysAhead))
    candidate = easternDateTimeToUtc(
      nextCalendarDate.getUTCFullYear(),
      nextCalendarDate.getUTCMonth() + 1,
      nextCalendarDate.getUTCDate(),
      show.hourEastern,
    )
  }

  return candidate
}
